import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Trophy, 
  Medal, 
  Award, 
  Crown, 
  Users, 
  Clock, 
  TrendingUp,
  RefreshCw,
  Target
} from 'lucide-react';

const BACKEND_URL = window.location.origin;

const Leaderboard = ({ gameId, gameName, playerData, onPlayerRankUpdate }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameStats, setGameStats] = useState(null);
  const [playerRank, setPlayerRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [prizes, setPrizes] = useState([]);

  // Fetch prizes from API
  const fetchPrizes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prizes`);
      if (response.ok) {
        const data = await response.json();
        setPrizes(data.prizes || []);
      }
    } catch (error) {
      console.error('Error fetching prizes:', error);
    }
  };

  // Fetch leaderboard data
  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leaderboard/${gameId}`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  // Fetch game statistics  
  const fetchGameStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/games/${gameId}/stats`);
      if (response.ok) {
        const data = await response.json();
        setGameStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch game stats:', error);
    }
  };

  // Fetch player's current rank
  const fetchPlayerRank = async () => {
    if (!playerData?.id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leaderboard/${gameId}/player/${playerData.id}`);
      if (response.ok) {
        const data = await response.json();
        setPlayerRank(data);
        if (onPlayerRankUpdate) {
          onPlayerRankUpdate(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch player rank:', error);
    }
  };

  // Refresh all data
  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      fetchLeaderboard(),
      fetchGameStats(), 
      fetchPlayerRank(),
      fetchPrizes()
    ]);
    setLoading(false);
    setLastUpdate(new Date());
  };

  // Initial load and periodic updates
  useEffect(() => {
    refreshData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshData, 30000);
    return () => clearInterval(interval);
  }, [gameId, playerData?.id]);

  // Get rank icon and color
  const getRankDisplay = (rank) => {
    if (rank === 1) return { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-400/20' };
    if (rank === 2) return { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-300/20' };
    if (rank === 3) return { icon: Award, color: 'text-orange-400', bg: 'bg-orange-400/20' };
    return { icon: Target, color: 'text-blue-400', bg: 'bg-blue-400/20' };
  };

  // Get prize for position from API data
  const getPrize = (rank) => {
    const prize = prizes.find(p => p.position === rank);
    return prize ? prize.prize_name : null;
  };

  return (
    <div className="space-y-6">
      {/* Game Statistics */}
      {gameStats && (
        <Card className="gaming-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-white flex items-center space-x-2">
                <Trophy className="w-6 h-6 text-yellow-400" />
                <span>{gameName} Competition</span>
              </CardTitle>
              <Button 
                onClick={refreshData} 
                disabled={loading}
                className="btn-secondary px-3 py-1 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{gameStats.total_players_today}</div>
                <div className="text-gray-400 text-sm">Players Today</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">{gameStats.time_left}</div>
                <div className="text-gray-400 text-sm">Time Left</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{leaderboard.length}</div>
                <div className="text-gray-400 text-sm">Total Scores</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">3</div>
                <div className="text-gray-400 text-sm">Prize Winners</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Player's Current Position */}
      {playerRank && playerRank.rank && (
        <Card className="gaming-card border-yellow-400/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
              <span>Your Current Position</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">#{playerRank.rank}</div>
                  <div className="text-gray-400 text-sm">Your Rank</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{playerRank.score.toLocaleString()}</div>
                  <div className="text-gray-400 text-sm">Your Score</div>
                </div>
                {playerRank.accuracy && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-400">{playerRank.accuracy}%</div>
                    <div className="text-gray-400 text-sm">Accuracy</div>
                  </div>
                )}
              </div>
              {playerRank.rank <= 3 && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                  🏆 Prize Position: {getPrize(playerRank.rank)}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Leaderboard */}
      <Card className="gaming-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-white">Live Leaderboard</CardTitle>
            <div className="text-sm text-gray-400">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-4" />
              <div className="text-gray-400">Loading leaderboard...</div>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <div className="text-gray-400 text-lg">No players yet</div>
              <div className="text-gray-500 text-sm">Be the first to play and claim #1!</div>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.slice(0, 20).map((entry) => {
                const rankDisplay = getRankDisplay(entry.rank);
                const RankIcon = rankDisplay.icon;
                const prize = getPrize(entry.rank);
                const isCurrentPlayer = playerData?.id === entry.player_id;
                
                return (
                  <div 
                    key={entry.player_id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all duration-300 ${
                      isCurrentPlayer 
                        ? 'bg-yellow-400/20 border-2 border-yellow-400/50' 
                        : entry.rank <= 3 
                          ? `${rankDisplay.bg} border border-gray-600`
                          : 'bg-gray-800/50 border border-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full ${rankDisplay.bg}`}>
                        <RankIcon className={`w-5 h-5 ${rankDisplay.color}`} />
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-white">#{entry.rank}</span>
                          <span className="font-semibold text-gray-300">{entry.masked_name}</span>
                          <span className="text-sm text-gray-500">({entry.city})</span>
                          {isCurrentPlayer && (
                            <Badge className="bg-yellow-400/20 text-yellow-400 text-xs">YOU</Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span>Score: {entry.score.toLocaleString()}</span>
                          {entry.accuracy && <span>Accuracy: {entry.accuracy}%</span>}
                          <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      {prize && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-1">
                          🎁 {prize}
                        </Badge>
                      )}
                      <div className="text-lg font-bold text-white">{entry.score.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
              
              {leaderboard.length > 20 && (
                <div className="text-center py-4 text-gray-400">
                  ... and {leaderboard.length - 20} more players
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Leaderboard;